import { Button, Stack } from '@mui/material';
import type { LucideIcon } from 'lucide-react';
import { useFormContext } from 'react-hook-form';

import { RaLabel } from '@modules/_shared/components/form/RaLabel';
import { useLocale } from '@shared/hooks/locale';

export type StatusOption<T extends string> = {
  value: T;
  icon: LucideIcon;
  getLabel: (t: (key: string) => string) => string;
};

type StatusButtonsProps<TStatus extends string> = {
  name: string;
  options: Array<StatusOption<TStatus>>;
};

export const StatusButtons = <TStatus extends string>({
  name,
  options,
}: StatusButtonsProps<TStatus>) => {
  const { t } = useLocale();
  const { watch, setValue } = useFormContext();
  const currentStatus = watch(name) as TStatus | undefined;

  return (
    <Stack>
      <RaLabel label={t('Status')} />
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        sx={{
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        {options.map(({ value, icon: Icon, getLabel }) => (
          <Button
            key={value}
            variant="outlined"
            sx={{
              whiteSpace: 'nowrap',
              backgroundColor:
                currentStatus === value
                  ? 'primary.lighter'
                  : 'background.paper',
              fontWeight: 'normal',
            }}
            color={currentStatus === value ? undefined : 'neutral'}
            onClick={() =>
              setValue(name, value, {
                shouldDirty: true,
              })
            }
            startIcon={<Icon size={16} />}
          >
            {getLabel(t)}
          </Button>
        ))}
      </Stack>
    </Stack>
  );
};
